import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { Prescription, PrescriptionTypes, parseReason } from '@lib/prescriptions/data-access';
import { TranslatePipe } from '@ngx-translate/core';

import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { StatusComponent } from '@lib/shared-ui/commons/status/status.component';
import { ObjectChange } from '@lib/shared-ui/core/utils/generate-object-changes.function';

interface FieldChange {
  fieldName: string;
  label: string;
  oldValue: string;
  newValue: string;
}

@Component({
  selector: 'prescriptions-changes-list-item',
  standalone: true,
  imports: [StatusComponent, TranslatePipe],
  templateUrl: './prescription-changes-list-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrescriptionChangesListItemComponent {
  protected readonly prescriptionTypes = PrescriptionTypes;
  protected readonly formatDate = formatDate;

  readonly prescriptionChange = input.required<ObjectChange<Prescription>>();

  protected readonly changedFields = computed<FieldChange[]>(() => {
    const change = this.prescriptionChange();
    const oldObj = change.oldObject;
    const newObj = change.newObject;
    const oldMetadata = oldObj.metadata || {};
    const newMetadata = newObj.metadata || {};
    const parsedReason = parseReason(newObj.reason || '');
    const changes: FieldChange[] = [];

    // Compare name
    if (oldObj.name !== newObj.name) {
      changes.push({
        fieldName: 'name',
        label: 'Назва',
        oldValue: oldObj.name || '',
        newValue: newObj.name || '',
      });
    }

    // Compare metadata quantity for service
    if (oldMetadata['quantity'] !== newMetadata['quantity']) {
      changes.push({
        fieldName: 'metadata.quantity',
        label: 'Загальна кількість',
        oldValue: oldMetadata['quantity']?.toString() || '',
        newValue: newMetadata['quantity']?.toString() || '',
      });
    }

    // Compare metadata treatment_dose for medication
    if (oldMetadata['treatment_dose'] !== newMetadata['treatment_dose']) {
      changes.push({
        fieldName: 'metadata.treatment_dose',
        label: 'Загальна кількість',
        oldValue: oldMetadata['treatment_dose'] ? oldMetadata['treatment_dose'].toString() + ' од.' : '',
        newValue: newMetadata['treatment_dose'] ? newMetadata['treatment_dose'].toString() + ' од.' : '',
      });
    }

    // Compare metadata single_dose for medication
    if (oldMetadata['single_dose'] !== newMetadata['single_dose']) {
      changes.push({
        fieldName: 'metadata.single_dose',
        label: 'Разова доза',
        oldValue: oldMetadata['single_dose']?.toString() || '',
        newValue: newMetadata['single_dose']?.toString() || '',
      });
    }

    // Compare metadata daily_amount for medication
    if (oldMetadata['daily_amount'] !== newMetadata['daily_amount']) {
      changes.push({
        fieldName: 'metadata.daily_amount',
        label: 'Щоденна',
        oldValue: oldMetadata['daily_amount'] ? oldMetadata['daily_amount'].toString() + '/день' : '',
        newValue: newMetadata['daily_amount'] ? newMetadata['daily_amount'].toString() + '/день' : '',
      });
    }

    // Compare metadata treatment_packages for medication
    if (oldMetadata['treatment_packages'] !== newMetadata['treatment_packages']) {
      changes.push({
        fieldName: 'metadata.treatment_packages',
        label: 'Кількість в упаковці',
        oldValue: oldMetadata['treatment_packages']?.toString() || '',
        newValue: newMetadata['treatment_packages']?.toString() || '',
      });
    }

    // Compare startDate
    if (oldObj.startDate !== newObj.startDate) {
      changes.push({
        fieldName: 'startDate',
        label: 'Дата з',
        oldValue: oldObj.startDate ? this.formatDate(oldObj.startDate, true) : '',
        newValue: newObj.startDate ? this.formatDate(newObj.startDate, true) : '',
      });
    }

    // Compare endDate
    if (oldObj.endDate !== newObj.endDate) {
      changes.push({
        fieldName: 'endDate',
        label: 'Дата до',
        oldValue: oldObj.endDate ? this.formatDate(oldObj.endDate, true) : '',
        newValue: newObj.endDate ? this.formatDate(newObj.endDate, true) : '',
      });
    }

    // Compare franchise
    if (oldObj.franchise !== newObj.franchise) {
      changes.push({
        fieldName: 'franchise',
        label: 'Франшиза',
        oldValue: oldObj.franchise.toString() || '',
        newValue: newObj.franchise.toString() || '',
      });
    }

    // If the parsed edit reason is not empty, push it
    if (parsedReason.editReason && parsedReason.editReason !== newObj.completionReason) {
      changes.push({
        fieldName: 'editReason',
        label: 'Причина редагування',
        oldValue: '',
        newValue: parsedReason.editReason,
      });
    }

    // Compare guaranteeStatus
    if (oldObj.guaranteeStatus !== newObj.guaranteeStatus) {
      changes.push({
        fieldName: 'guaranteeStatus',
        label: 'Статус',
        oldValue: oldObj.guaranteeStatus || '',
        newValue: newObj.guaranteeStatus || '',
      });

      // If the parsed status reason is not empty, push it
      if (parsedReason.statusReason) {
        changes.push({
          fieldName: 'statusReason',
          label: 'Причина статусу',
          oldValue: '',
          newValue: parsedReason.statusReason,
        });
      }
    }

    // Always push, old lastUpdateByName is not needed
    changes.push({
      fieldName: 'lastUpdateByName',
      label: 'Відповідальний',
      oldValue: '',
      newValue: newObj.lastUpdateByName || '',
    });

    // If parsed reason is legacy, push it
    if (parsedReason.legacyReason) {
      changes.push({
        fieldName: 'reason',
        label: 'Причина змін',
        oldValue: '',
        newValue: parsedReason.legacyReason,
      });
    }

    // If the completion reason is not empty, push it
    if (newObj.completionReason) {
      changes.push({
        fieldName: 'completionReason',
        label: 'Причина виконання',
        oldValue: '',
        newValue: newObj.completionReason,
      });
    }

    return changes;
  });
}
